import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { Col, Divider, Checkbox, Form, FormInstance } from 'antd';

import CustomRadioGroup from '@/components/CustomRadioGroup';
import {
  ChevronDownIcon,
  ChevronUpIcon,
  StarTickIcon,
  UndoIcon,
} from '@/assets/icons/icons';
import { AgentDrawerFormValues } from '../types';
import CheckboxMultiSelect from '@/components/CheckboxMultiSelect';
import { CustomButton } from '@/components';
import { LoadingIcon } from './LoadingIcon';

type Option = { value: string; label: string };
type SuccessMode = 'specific' | 'custom';

type Props = {
  form: FormInstance<AgentDrawerFormValues>;
  loadingDropdowns?: boolean;
  collectOptions?: Option[];
  defaultCollapsed?: boolean;
  onOptimize?: () => void;
  behaviorInstructions?: string;
  isOptimizing?: boolean;
  lastOptimizedResolution?: string | null;
  readOnly?: boolean;
};

export const SuccessSection: React.FC<Props> = ({
  form,
  loadingDropdowns = false,
  collectOptions = [],
  defaultCollapsed = false,
  onOptimize,
  behaviorInstructions,
  isOptimizing = false,
  lastOptimizedResolution = null,
  readOnly = false,
}) => {
  const [collapsed, setCollapsed] = useState<boolean>(defaultCollapsed);

  const mode = (Form.useWatch('success_mode', form) ??
    'specific') as SuccessMode;
  const infoCollected = Boolean(
    Form.useWatch('success_info_collected', form) ?? false
  );
  const noHuman = Boolean(Form.useWatch('success_no_human', form) ?? false);
  const positive = Boolean(Form.useWatch('success_positive', form) ?? false);

  const collectSelected =
    (Form.useWatch('success_collect_selected', form) as string[] | undefined) ??
    [];
  const customText = String(Form.useWatch('success_custom_text', form) ?? '');

  useEffect(() => {
    setCollapsed(defaultCollapsed);
  }, [defaultCollapsed]);

  const canOptimize = useMemo(() => {
    const hasChanged = lastOptimizedResolution
      ? customText !== lastOptimizedResolution
      : true;

    return (
      mode === 'custom' &&
      customText.trim().length > 0 &&
      (behaviorInstructions?.trim().length ?? 0) > 0 &&
      !isOptimizing &&
      hasChanged
    );
  }, [
    mode,
    customText,
    behaviorInstructions,
    isOptimizing,
    lastOptimizedResolution,
  ]);

  const toggleCollapsed = useCallback(() => {
    setCollapsed((p) => !p);
  }, []);

  const stop = (e: React.SyntheticEvent) => e.stopPropagation();

  const setMode = (next: SuccessMode) => {
    form.setFieldsValue({ success_mode: next });
  };

  const setInfoCollected = (checked: boolean) => {
    form.setFieldsValue({
      success_info_collected: checked,
      ...(checked ? {} : { success_collect_selected: [] }),
    });
  };

  const setNoHuman = (checked: boolean) => {
    form.setFieldsValue({ success_no_human: checked });
  };

  const setPositive = (checked: boolean) => {
    form.setFieldsValue({ success_positive: checked });
  };

  const setCollectSelected = (vals: string[]) => {
    form.setFieldsValue({ success_collect_selected: vals });
  };

  const setCustomText = (text: string) => {
    form.setFieldsValue({ success_custom_text: text });
  };

  return (
    <Col span={24}>
      <div className='agentSuccessCard'>
        <div className='agentSuccessHeader'>
          <div className='agentSuccessHeader__title'>
            Agent success criteria
          </div>

          <span
            className='agentSuccessHeader__chevron'
            role='button'
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              toggleCollapsed();
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                toggleCollapsed();
              }
            }}
          >
            {collapsed ? (
              <ChevronDownIcon size={16} />
            ) : (
              <ChevronUpIcon size={16} />
            )}
          </span>
        </div>

        {!collapsed && (
          <div
            className='agentSuccessBody'
            onMouseDown={stop}
            onClick={stop}
            onPointerDown={stop}
          >
            <Divider className='agentSuccessHeaderDivider' />
            <div className='agentSuccessRow'>
              <CustomRadioGroup
                value={mode}
                onChange={(e) => setMode(e.target.value as SuccessMode)}
                options={[
                  { value: 'specific', label: 'Specific success criteria' },
                ]}
                disabled={readOnly}
              />
            </div>

            {mode === 'specific' && (
              <div className='agentSuccessSpecific'>
                <div className='agentSuccessCheckRow'>
                  <Checkbox
                    className='custom-menu-shipment-checkbox-only'
                    checked={infoCollected}
                    onChange={(e) => setInfoCollected(e.target.checked)}
                    disabled={readOnly}
                  >
                    <span className='agentSuccessCheckLabel'>
                      Information successfully collected:
                    </span>
                  </Checkbox>
                </div>

                <CheckboxMultiSelect
                  titleText='Information successfully collected:'
                  options={collectOptions}
                  placeholder={loadingDropdowns ? 'Loading...' : 'Select...'}
                  className='agentSelect agentSelect--darkText'
                  disabled={readOnly || !infoCollected}
                  value={collectSelected}
                  onChange={(vals) =>
                    setCollectSelected((vals || []) as string[])
                  }
                />
                <div className='agentSuccessCheckRow'>
                  <Checkbox
                    className='custom-menu-shipment-checkbox-only'
                    checked={noHuman}
                    onChange={(e) => setNoHuman(e.target.checked)}
                    disabled={readOnly}
                  >
                    <span className='agentSuccessCheckLabel'>
                      No human intervention was needed
                    </span>
                  </Checkbox>
                </div>

                <div className='agentSuccessCheckRow'>
                  <Checkbox
                    className='custom-menu-shipment-checkbox-only'
                    checked={positive}
                    onChange={(e) => setPositive(e.target.checked)}
                    disabled={readOnly}
                  >
                    <span className='agentSuccessCheckLabel'>
                      Neutral or positive sentiment
                    </span>
                  </Checkbox>
                </div>

                <div className='agentSuccessHelper'>Select all that apply</div>
              </div>
            )}

            <div className='agentSuccessOrRow'>
              <Divider className='agentSuccessOrDivider'>OR</Divider>
            </div>

            <div className='agentSuccessRow agentSuccessRow--custom'>
              <div className='agentSuccessCustomHeader'>
                <CustomRadioGroup
                  value={mode}
                  onChange={(e) => setMode(e.target.value as SuccessMode)}
                  options={[
                    { value: 'custom', label: 'Custom success criteria' },
                  ]}
                  disabled={readOnly}
                />

                {!readOnly &&
                  mode === 'custom' &&
                  customText.trim().length > 0 && (
                    <button
                      type='button'
                      className='agentSuccessResetBtn'
                      onClick={(e) => {
                        e.stopPropagation();
                        setCustomText('');
                      }}
                      aria-label='Reset custom success criteria'
                    >
                      <UndoIcon size={16} />
                    </button>
                  )}
              </div>
            </div>

            {mode === 'custom' && (
              <div className='agentSuccessCustom'>
                <div className='agentSuccessTextareaShell'>
                  <textarea
                    className='agentSuccessTextarea'
                    placeholder='Example: Success if delivery is confirmed, or a confirmed ETA (date + time window) is captured'
                    value={customText}
                    onChange={(e) => setCustomText(e.target.value)}
                    disabled={isOptimizing || readOnly}
                  />
                  {isOptimizing && (
                    <div className='agentSuccessLoadingOverlay'>
                      <LoadingIcon />
                    </div>
                  )}
                </div>

                <div className='agentSuccessSubHelper'>
                  Success if [primary outcome], or [fallback outcome]
                </div>

                {!readOnly && (
                  <CustomButton
                    text='Optimize instructions'
                    onClick={() => {
                      if (!canOptimize) return;
                      onOptimize?.();
                    }}
                    width='196px'
                    height='36px'
                    bgColor={
                      canOptimize ? 'var(--text-primary)' : 'var(--gray-300)'
                    }
                    color={
                      canOptimize
                        ? 'var(--surface-container)'
                        : 'var(--text-tertiary)'
                    }
                    borderRadius='8px'
                    fontSize='14px'
                    fontWeight='600'
                    boxShadow='none'
                    marginTop='16px'
                    iconPosition='left'
                    icon={
                      <StarTickIcon
                        color={
                          canOptimize
                            ? 'var(--surface-container)'
                            : 'var(--text-tertiary)'
                        }
                      />
                    }
                    isFormValid={!canOptimize}
                    gap='8px'
                    className='agentSuccessOptimizeCustomBtn'
                  />
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </Col>
  );
};

export default SuccessSection;
